import React from "react";
import { ChevronRight, Search, X, Info, AlertTriangle, CheckCircle2, XCircle } from "lucide-react";

// Fiori-style building blocks for the Hasbro workspace (Horizon-like layout and semantic
// colours, in Hasbro blue). Light + dark via Tailwind `dark:`.

export const surface = "bg-white dark:bg-[#18212C] border border-slate-200/80 dark:border-white/[0.07] rounded-2xl shadow-[0_1px_2px_rgba(16,24,40,0.04)]";
export const inputCls = "w-full h-9 px-3 rounded-lg border border-slate-300 dark:border-white/10 bg-white dark:bg-[#0F161E] text-sm text-slate-900 dark:text-slate-100 placeholder-slate-400 outline-none focus:border-[#0087C9] focus:ring-2 focus:ring-[#00B7EB]/25 transition";

// Semantic tones (Fiori: information / positive / critical / negative / neutral / indication)
export const TONES = {
    information: { chip: "bg-[#E5F4FC] text-[#00609A] dark:bg-[#0087C9]/15 dark:text-[#6FD0F5]", bar: "bg-[#0087C9]", dot: "bg-[#0087C9]" },
    positive:    { chip: "bg-emerald-50 text-emerald-700 dark:bg-emerald-400/15 dark:text-emerald-300", bar: "bg-emerald-500", dot: "bg-emerald-500" },
    critical:    { chip: "bg-amber-50 text-amber-700 dark:bg-amber-400/15 dark:text-amber-300", bar: "bg-amber-500", dot: "bg-amber-500" },
    negative:    { chip: "bg-red-50 text-red-700 dark:bg-red-400/15 dark:text-red-300", bar: "bg-red-500", dot: "bg-red-500" },
    neutral:     { chip: "bg-slate-100 text-slate-600 dark:bg-white/[0.08] dark:text-slate-300", bar: "bg-slate-400", dot: "bg-slate-400" },
    indication:  { chip: "bg-violet-50 text-violet-700 dark:bg-violet-400/15 dark:text-violet-300", bar: "bg-violet-500", dot: "bg-violet-500" },
};

export function PageHeader({ breadcrumb = [], title, subtitle, icon: Icon, actions, children }) {
    return (
        <header className="space-y-5">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
                <div className="min-w-0">
                    {breadcrumb.length > 0 && (
                        <nav className="flex items-center gap-1 text-xs text-slate-500 dark:text-slate-400 mb-2">
                            {breadcrumb.map((b, i) => (
                                <React.Fragment key={i}>
                                    {i > 0 && <ChevronRight size={12} className="opacity-60" />}
                                    {b.onClick
                                        ? <button onClick={b.onClick} className="hover:text-[#0087C9] dark:hover:text-[#6FD0F5]">{b.label}</button>
                                        : <span>{b.label}</span>}
                                </React.Fragment>
                            ))}
                        </nav>
                    )}
                    <div className="flex items-center gap-3">
                        {Icon && (
                            <span className="w-10 h-10 rounded-xl grid place-items-center bg-gradient-to-br from-[#00B7EB] to-[#0065A0] text-white shadow-sm shrink-0">
                                <Icon size={20} />
                            </span>
                        )}
                        <h1 className="text-2xl font-bold tracking-tight text-[#1D2D3E] dark:text-white truncate">{title}</h1>
                    </div>
                    {subtitle && <p className="text-sm text-slate-500 dark:text-slate-400 mt-1.5 max-w-2xl">{subtitle}</p>}
                </div>
                {actions && <div className="flex items-center gap-2 shrink-0">{actions}</div>}
            </div>
            {children}
        </header>
    );
}

export function Button({ variant = "default", icon: Icon, children, className = "", ...props }) {
    const styles = {
        emphasized: "bg-[#0087C9] hover:bg-[#0074AD] active:bg-[#0065A0] text-white shadow-sm",
        default: "bg-white dark:bg-white/[0.04] border border-slate-300 dark:border-white/10 text-[#0065A0] dark:text-[#6FD0F5] hover:bg-slate-50 dark:hover:bg-white/[0.08]",
        transparent: "text-[#0065A0] dark:text-[#6FD0F5] hover:bg-[#0087C9]/10",
        negative: "bg-white dark:bg-white/[0.04] border border-red-200 dark:border-red-400/30 text-red-600 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-400/10",
    };
    return (
        <button
            {...props}
            className={`inline-flex items-center justify-center gap-1.5 h-9 ${children ? "px-3.5" : "w-9"} rounded-lg text-sm font-semibold transition disabled:opacity-50 disabled:pointer-events-none focus:outline-none focus-visible:ring-2 focus-visible:ring-[#00B7EB]/50 ${styles[variant]} ${className}`}
        >
            {Icon && <Icon size={15} className="shrink-0" />}
            {children}
        </button>
    );
}

// KPI tile with a share-of-total micro bar (Fiori comparison micro chart)
export function KpiTile({ label, value, total, tone = "information", icon: Icon, active, onClick, hint }) {
    const t = TONES[tone];
    const pct = total ? Math.round((value / total) * 100) : 0;
    const Comp = onClick ? "button" : "div";
    return (
        <Comp onClick={onClick}
            className={`${surface} text-left p-4 flex flex-col gap-3 transition ${onClick ? "hover:-translate-y-0.5 hover:shadow-md cursor-pointer" : ""} ${active ? "ring-2 ring-[#0087C9] dark:ring-[#00B7EB]" : ""}`}>
            <div className="flex items-center justify-between gap-2">
                <span className="text-xs font-semibold text-slate-500 dark:text-slate-400 truncate">{label}</span>
                {Icon && <span className={`w-7 h-7 rounded-lg grid place-items-center ${t.chip}`}><Icon size={14} /></span>}
            </div>
            <div className="flex items-baseline gap-2">
                <span className="text-3xl font-bold tracking-tight text-[#1D2D3E] dark:text-white tabular-nums">{value}</span>
                {hint && <span className="text-xs text-slate-400">{hint}</span>}
            </div>
            {total !== undefined && (
                <div className="h-1.5 rounded-full bg-slate-100 dark:bg-white/[0.06] overflow-hidden">
                    <div className={`h-full rounded-full ${t.bar} transition-all duration-500`} style={{ width: `${pct}%` }} />
                </div>
            )}
        </Comp>
    );
}

export function ObjectStatus({ tone = "neutral", icon: Icon, children }) {
    return (
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-semibold whitespace-nowrap ${TONES[tone].chip}`}>
            {Icon && <Icon size={12} />}{children}
        </span>
    );
}

const AVATAR_TONES = ["from-[#00B7EB] to-[#0065A0]", "from-[#00ABB3] to-[#00737A]", "from-violet-400 to-violet-700", "from-amber-400 to-orange-600", "from-rose-400 to-rose-700", "from-emerald-400 to-emerald-700"];
export function Avatar({ name = "?", size = 32, square = false }) {
    const initials = name.split(/[\s@._-]+/).filter(Boolean).slice(0, 2).map(w => w[0]).join("").toUpperCase() || "?";
    const tone = AVATAR_TONES[[...name].reduce((a, c) => a + c.charCodeAt(0), 0) % AVATAR_TONES.length];
    return (
        <span style={{ width: size, height: size, fontSize: size * 0.38 }}
            className={`shrink-0 grid place-items-center font-bold text-white bg-gradient-to-br ${tone} ${square ? "rounded-xl" : "rounded-full"}`}>
            {initials}
        </span>
    );
}

export function SearchField({ value, onChange, placeholder = "Search", className = "" }) {
    return (
        <div className={`relative ${className}`}>
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            <input value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} className={`${inputCls} pl-9 pr-8`} />
            {value && (
                <button onClick={() => onChange("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" title="Clear">
                    <X size={14} />
                </button>
            )}
        </div>
    );
}

export function SelectField({ label, value, onChange, children, className = "" }) {
    return (
        <label className={`flex flex-col gap-1 ${className}`}>
            {label && <span className="text-[11px] font-semibold text-slate-500 dark:text-slate-400">{label}</span>}
            <select value={value} onChange={e => onChange(e.target.value)} className={`${inputCls} pr-8 cursor-pointer`}>{children}</select>
        </label>
    );
}

export function Field({ label, hint, children }) {
    return (
        <label className="flex flex-col gap-1">
            <span className="text-xs font-semibold text-slate-600 dark:text-slate-300">{label}</span>
            {children}
            {hint && <span className="text-[11px] text-slate-400">{hint}</span>}
        </label>
    );
}

// Card with a table toolbar: "Title (count)" + actions
export function TableCard({ title, count, actions, children }) {
    return (
        <section className={`${surface} overflow-hidden`}>
            {(title || actions) && (
                <div className="flex items-center justify-between gap-3 px-5 py-3.5 border-b border-slate-100 dark:border-white/[0.06]">
                    <h2 className="text-sm font-bold text-[#1D2D3E] dark:text-white">
                        {title}{count !== undefined && <span className="ml-1.5 font-semibold text-slate-400">({count})</span>}
                    </h2>
                    {actions && <div className="flex items-center gap-1.5">{actions}</div>}
                </div>
            )}
            {children}
        </section>
    );
}

export const thCls = "px-4 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400 bg-slate-50/80 dark:bg-white/[0.02] whitespace-nowrap";
export const tdCls = "px-4 py-3 align-middle";

export function EmptyState({ icon: Icon, title, text, action }) {
    return (
        <div className="flex flex-col items-center text-center px-6 py-14">
            <span className="w-16 h-16 rounded-2xl grid place-items-center bg-gradient-to-br from-[#E5F4FC] to-[#CDEBFA] dark:from-[#0087C9]/20 dark:to-[#0065A0]/10 text-[#0087C9] dark:text-[#6FD0F5] mb-4">
                <Icon size={28} />
            </span>
            <p className="text-base font-bold text-[#1D2D3E] dark:text-white">{title}</p>
            {text && <p className="text-sm text-slate-500 dark:text-slate-400 mt-1 max-w-sm">{text}</p>}
            {action && <div className="mt-5">{action}</div>}
        </div>
    );
}

const STRIP = {
    information: { icon: Info, cls: "bg-[#E5F4FC] border-[#9AD8F4] text-[#00507F] dark:bg-[#0087C9]/10 dark:border-[#0087C9]/40 dark:text-[#9ADFF8]" },
    warning: { icon: AlertTriangle, cls: "bg-amber-50 border-amber-200 text-amber-900 dark:bg-amber-400/10 dark:border-amber-400/30 dark:text-amber-200" },
    success: { icon: CheckCircle2, cls: "bg-emerald-50 border-emerald-200 text-emerald-900 dark:bg-emerald-400/10 dark:border-emerald-400/30 dark:text-emerald-200" },
    error: { icon: XCircle, cls: "bg-red-50 border-red-200 text-red-800 dark:bg-red-400/10 dark:border-red-400/30 dark:text-red-200" },
};
export function MessageStrip({ type = "information", children, onClose }) {
    const { icon: Icon, cls } = STRIP[type];
    return (
        <div className={`flex items-start gap-2.5 px-4 py-3 rounded-xl border text-sm ${cls}`}>
            <Icon size={16} className="shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">{children}</div>
            {onClose && <button onClick={onClose} className="opacity-60 hover:opacity-100"><X size={15} /></button>}
        </div>
    );
}

export function Dialog({ title, onClose, children, footer, width = "max-w-lg" }) {
    return (
        <div className="fixed inset-0 z-50 bg-[#0B1F33]/50 backdrop-blur-sm flex items-center justify-center p-4" onMouseDown={e => e.target === e.currentTarget && onClose()}>
            <div className={`${surface} w-full ${width} shadow-2xl overflow-hidden`}>
                <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 dark:border-white/[0.06]">
                    <h2 className="text-base font-bold text-[#1D2D3E] dark:text-white">{title}</h2>
                    <button onClick={onClose} className="text-slate-400 hover:text-slate-600 dark:hover:text-white"><X size={18} /></button>
                </div>
                <div className="p-5 space-y-4 max-h-[70vh] overflow-auto">{children}</div>
                {footer && <div className="flex justify-end gap-2 px-5 py-3.5 bg-slate-50 dark:bg-white/[0.02] border-t border-slate-100 dark:border-white/[0.06]">{footer}</div>}
            </div>
        </div>
    );
}

export function relTime(iso) {
    if (!iso) return "—";
    const s = (Date.now() - new Date(iso).getTime()) / 1000;
    if (s < 60) return "just now";
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
    if (s < 7 * 86400) return `${Math.floor(s / 86400)}d ago`;
    return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
}
